import React from "react";
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

import { DurationOption } from "../../types";

interface DurationSelectorProps {
  options: DurationOption[];
  selectedHours: number | null;
  onSelect: (hours: number) => void;
  disabled?: boolean;
}

const DurationSelector: React.FC<DurationSelectorProps> = ({
  options,
  selectedHours,
  onSelect,
  disabled = false,
}) => {
  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: number | null,
  ) => {
    if (newValue !== null) {
      onSelect(newValue);
    }
  };

  const selectedOption = options.find((o) => o.hours === selectedHours);

  return (
    <Box>
      <Typography
        variant="subtitle2"
        sx={{
          color: "text.secondary",
          mb: 2,
        }}
      >
        Select Duration
      </Typography>
      <ToggleButtonGroup
        value={selectedHours}
        exclusive
        onChange={handleChange}
        aria-label="duration selection"
        disabled={disabled}
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          "& .MuiToggleButtonGroup-grouped": {
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "8px !important",
            m: 0,
            "&:not(:first-of-type)": {
              borderLeft: "1px solid",
              borderColor: "divider",
            },
          },
        }}
      >
        {options.map((option) => (
          <ToggleButton
            key={option.hours}
            value={option.hours}
            sx={{
              px: 2,
              py: 1.5,
              minWidth: 100,
              flexDirection: "column",
              gap: 0.5,
              "&.Mui-selected": {
                bgcolor: "primary.main",
                color: "#ffffff",
                borderColor: "primary.main",
                "&:hover": {
                  bgcolor: "primary.dark",
                },
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <AccessTimeIcon sx={{ fontSize: 16 }} />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                }}
              >
                {option.label}
              </Typography>
            </Box>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      {selectedOption && (
        <Paper
          elevation={0}
          sx={{
            mt: 3,
            p: 2,
            bgcolor: "primary.main",
            color: "#ffffff",
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Selected Duration
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                }}
              >
                {selectedOption.label}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default DurationSelector;
